/**
 * PlanPickerDialog — multi-tier upgrade picker.
 *
 * Shown when a trial admin clicks "Upgrade" on the trial banner, or when
 * a past-due admin clicks "Reactivate billing". Lists Starter / Growth /
 * Enterprise with monthly/yearly toggle, each with a Checkout button
 * that calls POST /api/payments/checkout and redirects to Stripe. The
 * Enterprise+ card routes to the existing contact-sales lead flow via
 * the `onContactSales` prop.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { paymentsAPI } from '../lib/api';

const CURRENCY_LABEL = { gbp: '£' };

function formatAmount(amountMinor, currency) {
  if (amountMinor == null) return '—';
  const major = amountMinor / 100;
  const symbol = CURRENCY_LABEL[currency] || '';
  // Whole pounds for round prices, two decimals otherwise.
  const text = Number.isInteger(major) ? String(major) : major.toFixed(2);
  return `${symbol}${text}`;
}

export default function PlanPickerDialog({ open, onOpenChange, onContactSales }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cadence, setCadence] = useState('monthly');
  const [checkoutTier, setCheckoutTier] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    paymentsAPI.getPlans()
      .then((r) => setPlans(r.data?.plans || []))
      .catch(() => toast.error('Could not load plans'))
      .finally(() => setLoading(false));
  }, [open]);

  const yearlyAvailable = useMemo(
    () => plans.some((p) => p.prices?.yearly),
    [plans],
  );

  const startCheckout = async (plan) => {
    if (plan.contact_sales) {
      onOpenChange(false);
      onContactSales?.();
      return;
    }
    const priceInfo = plan.prices?.[cadence];
    if (!priceInfo) {
      toast.error('This plan is not available for the selected billing cycle');
      return;
    }
    setCheckoutTier(plan.id);
    try {
      const { data } = await paymentsAPI.createCheckout(
        priceInfo.lookup_key,
        window.location.origin,
      );
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Could not open checkout. Please try again.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Checkout failed. Try again shortly.');
    } finally {
      setCheckoutTier(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#1A1A1A] border-white/10 text-[#F2F2F2] max-w-5xl"
        data-testid="plan-picker-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#F2F2F2]">Choose your Elara plan</DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Billed in GBP. Stripe will show your local currency at checkout. Cancel any time from the billing portal.
          </DialogDescription>
        </DialogHeader>

        {yearlyAvailable && (
          <div className="flex items-center gap-3 justify-end mb-2">
            <span className={`text-sm ${cadence === 'monthly' ? 'text-[#F2F2F2]' : 'text-[#A1A1AA]'}`}>Monthly</span>
            <Switch
              checked={cadence === 'yearly'}
              onCheckedChange={(v) => setCadence(v ? 'yearly' : 'monthly')}
              data-testid="plan-cadence-toggle"
            />
            <span className={`text-sm ${cadence === 'yearly' ? 'text-[#F2F2F2]' : 'text-[#A1A1AA]'}`}>
              Yearly <span className="text-[#1ABC9C] font-semibold">(save ~15%)</span>
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#1ABC9C]" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {plans.map((plan) => {
              const price = plan.prices?.[cadence];
              return (
                <div
                  key={plan.id}
                  data-testid={`plan-card-${plan.id}`}
                  className={`
                    relative flex flex-col rounded-xl p-5 border transition-all
                    ${plan.recommended
                      ? 'border-[#1ABC9C] bg-gradient-to-b from-[#1ABC9C]/10 to-[#2B2B2B]'
                      : 'border-white/10 bg-[#2B2B2B] hover:border-white/20'}
                  `}
                >
                  {plan.recommended && (
                    <Badge className="absolute -top-3 right-4 bg-[#1ABC9C] text-black">
                      <Sparkles className="w-3 h-3 mr-1" /> Recommended
                    </Badge>
                  )}
                  <h3 className="text-lg font-semibold text-[#F2F2F2]">{plan.label}</h3>
                  <p className="text-sm text-[#A1A1AA] mt-1 min-h-[48px]">{plan.description}</p>

                  <div className="mt-4 min-h-[72px]">
                    {plan.contact_sales ? (
                      <>
                        <div className="text-2xl font-bold text-[#F2F2F2]">Custom</div>
                        <div className="text-xs text-[#A1A1AA]">Procurement & custom contracts</div>
                      </>
                    ) : price ? (
                      <>
                        <div className="text-3xl font-bold text-[#F2F2F2]">
                          {formatAmount(price.amount_minor, price.currency)}
                          <span className="text-sm font-normal text-[#A1A1AA]">
                            {' '}/ {cadence === 'yearly' ? 'year' : 'month'}
                          </span>
                        </div>
                        {plan.included_seats && (
                          <div className="text-xs text-[#A1A1AA] mt-1">
                            Up to {plan.included_seats} users included
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-[#A1A1AA]">Not available in this cycle.</div>
                    )}
                  </div>

                  <ul className="mt-3 space-y-2 flex-1">
                    {(plan.features || []).map((feat) => (
                      <li key={feat} className="flex items-start text-sm text-[#C7C7C7]">
                        <Check className="w-4 h-4 text-[#1ABC9C] mr-2 mt-0.5 flex-shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    data-testid={`plan-select-${plan.id}`}
                    disabled={checkoutTier === plan.id || (!price && !plan.contact_sales)}
                    onClick={() => startCheckout(plan)}
                    className={`
                      mt-5 w-full
                      ${plan.recommended
                        ? 'bg-[#1ABC9C] text-black hover:bg-[#1ABC9C]/90'
                        : plan.contact_sales
                          ? 'bg-transparent border border-white/20 text-[#F2F2F2] hover:bg-white/5'
                          : 'bg-white/10 text-[#F2F2F2] hover:bg-white/20'}
                    `}
                  >
                    {checkoutTier === plan.id
                      ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
                      : plan.contact_sales
                        ? 'Contact sales'
                        : 'Choose plan'}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
